import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Contract that components must implement to participate in the
 * `PendingChangesGuard` deactivation check.
 * Return `true` (or an `Observable<true>`) to allow navigation;
 * return `false` to block it (the guard will show a browser confirm dialog).
 */
export interface ComponentCanDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

/**
 * Route guard that prevents accidental navigation away from a form with
 * unsaved changes.
 *
 * The protected route must use a component that implements
 * {@link ComponentCanDeactivate}.  If `component.canDeactivate()` returns
 * `false`, the guard shows a `window.confirm` dialog asking the user to
 * confirm that unsaved changes will be discarded.
 *
 * Note: the confirm dialog is only shown for in-app navigation (Angular
 * Router).  Browser-level navigation (tab close, address-bar entry) triggers
 * the browser's own generic warning via `@HostListener('window:beforeunload')`.
 */
@Injectable({
  providedIn: 'root'
})
export class PendingChangesGuard  {
  /** Delegates to `component.canDeactivate()`; shows a confirm prompt on `false`. */
  canDeactivate(component: ComponentCanDeactivate): boolean | Observable<boolean> {
    return component.canDeactivate() ?
      true :
      confirm('ВНИМАНИЕ: На странице имеются несохраненные изменения. Нажмите Cancel для возврата на страницу и их сохранения, или OK для продложения (изменения будут утрачены).');
  }
}
